import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  User, Trophy, Target, TrendingUp, Star, Calendar,
  Award, Zap, Crown, ChevronRight, Shield, Upload,
  CheckCircle, XCircle, Clock, AlertCircle, Loader2
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
    fetchKycStatus();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/me`, { withCredentials: true });
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchKycStatus = async () => {
    try {
      const response = await axios.get(`${API}/kyc/status`, { withCredentials: true });
      setKycStatus(response.data);
    } catch (error) {
      console.error('Error fetching KYC status:', error);
    }
  };

  const handleKycUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or PDF file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);

      await axios.post(`${API}/kyc/submit`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('KYC document submitted for review!');
      await fetchKycStatus();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit KYC');
    } finally {
      setUploading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: Crown, color: '#FFD700', label: 'Champion' };
    if (rank <= 3) return { icon: Trophy, color: '#C0C0C0', label: 'Elite' };
    if (rank <= 10) return { icon: Star, color: '#CD7F32', label: 'Pro' };
    if (rank <= 50) return { icon: Award, color: '#7000FF', label: 'Skilled' };
    return { icon: Zap, color: '#00F0FF', label: 'Rising' };
  };

  const getEloTier = (elo) => {
    if (elo >= 2000) return { label: 'Grandmaster', color: '#FFD700' };
    if (elo >= 1800) return { label: 'Master', color: '#7000FF' };
    if (elo >= 1600) return { label: 'Diamond', color: '#00F0FF' };
    if (elo >= 1400) return { label: 'Platinum', color: '#00FF94' };
    if (elo >= 1200) return { label: 'Gold', color: '#CCFF00' };
    if (elo >= 1000) return { label: 'Silver', color: '#C0C0C0' };
    return { label: 'Bronze', color: '#CD7F32' };
  };

  const getKycStatusConfig = (status) => {
    switch (status) {
      case 'approved':
        return { icon: CheckCircle, color: '#00FF94', bg: 'bg-[#00FF94]/10', text: 'Verified' };
      case 'pending':
        return { icon: Clock, color: '#FFB800', bg: 'bg-[#FFB800]/10', text: 'Pending Review' };
      case 'rejected':
        return { icon: XCircle, color: '#FF2E2E', bg: 'bg-[#FF2E2E]/10', text: 'Rejected' };
      default:
        return { icon: AlertCircle, color: '#FFFFFF', bg: 'bg-white/10', text: 'Not Verified' };
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const stats = profile?.stats || {};
  const userData = profile?.user || user;
  const rankBadge = getRankBadge(stats.rank);
  const userElo = userData?.elo_rating || 1200;
  const eloTier = getEloTier(userElo);
  const kycConfig = getKycStatusConfig(kycStatus?.status);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="profile-page">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8 mb-6 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#7000FF]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          
          <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-6">
            <Avatar className="w-24 h-24 border-4 border-[#7000FF]">
              <AvatarImage src={userData?.picture} />
              <AvatarFallback className="bg-[#7000FF] text-white text-3xl">
                {userData?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-2 flex-wrap">
                <h1 className="font-heading text-3xl font-bold">{userData?.name}</h1>
                {stats.rank > 0 && (
                  <div 
                    className="flex items-center gap-1 px-3 py-1 rounded-full"
                    style={{ backgroundColor: `${rankBadge.color}20`, color: rankBadge.color }}
                  >
                    <rankBadge.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{rankBadge.label}</span>
                  </div>
                )}
                {kycStatus?.is_verified && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#00FF94]/20 text-[#00FF94]">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">KYC Verified</span>
                  </div>
                )}
              </div>
              <p className="text-white/50 mb-4">{userData?.email || userData?.username}</p>
              
              <div className="flex flex-wrap justify-center md:justify-start gap-6">
                <div>
                  <p className="text-white/50 text-sm">ELO Rating</p>
                  <p className="font-mono text-2xl font-bold" style={{ color: eloTier.color }}>
                    {userElo}
                  </p>
                  <p className="text-xs" style={{ color: eloTier.color }}>{eloTier.label}</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm">Global Rank</p>
                  <p className="font-mono text-2xl font-bold text-[#CCFF00]">
                    {stats.rank > 0 ? `#${stats.rank}` : 'Unranked'}
                  </p>
                </div>
                <div>
                  <p className="text-white/50 text-sm">Win Rate</p>
                  <p className="font-mono text-2xl font-bold text-[#00FF94]">{stats.win_rate}%</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm">Total Earnings</p>
                  <p className="font-mono text-2xl font-bold text-[#00F0FF]">
                    ${(stats.total_earnings || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* KYC Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card rounded-xl p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#00F0FF]" />
              <h2 className="font-heading font-semibold text-lg">Identity Verification (KYC)</h2>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${kycConfig.bg}`}>
              <kycConfig.icon className="w-4 h-4" style={{ color: kycConfig.color }} />
              <span className="text-sm" style={{ color: kycConfig.color }}>{kycConfig.text}</span>
            </div>
          </div>

          {kycStatus?.status === 'none' && (
            <div className="text-center py-6">
              <p className="text-white/50 mb-4">
                Verify your identity to unlock higher withdrawal limits and build trust with opponents.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={handleKycUpload}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="btn-primary"
                data-testid="kyc-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload ID Document
                  </>
                )}
              </Button>
              <p className="text-white/30 text-xs mt-2">Accepted: JPG, PNG, PDF (max 5MB)</p>
            </div>
          )}

          {kycStatus?.status === 'pending' && (
            <div className="text-center py-6">
              <Clock className="w-12 h-12 text-[#FFB800] mx-auto mb-3" />
              <p className="text-white/70">Your document is being reviewed by our team.</p>
              <p className="text-white/40 text-sm mt-1">This usually takes 24-48 hours.</p>
            </div>
          )}

          {kycStatus?.status === 'approved' && (
            <div className="text-center py-6">
              <CheckCircle className="w-12 h-12 text-[#00FF94] mx-auto mb-3" />
              <p className="text-[#00FF94] font-semibold">Your identity has been verified!</p>
              <p className="text-white/40 text-sm mt-1">
                Verified on {new Date(kycStatus.reviewed_at).toLocaleDateString()}
              </p>
            </div>
          )}

          {kycStatus?.status === 'rejected' && (
            <div className="text-center py-6">
              <XCircle className="w-12 h-12 text-[#FF2E2E] mx-auto mb-3" />
              <p className="text-[#FF2E2E] font-semibold mb-2">Verification rejected</p>
              <p className="text-white/50 text-sm mb-4">Please upload a clearer document.</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={handleKycUpload}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                variant="outline"
                className="btn-secondary"
              >
                <Upload className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          )}
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Matches', value: stats.total_matches, icon: Target, color: '#7000FF' },
            { label: 'Wins', value: stats.wins, icon: Trophy, color: '#00FF94' },
            { label: 'Losses', value: stats.losses, icon: Target, color: '#FF2E2E' },
            { label: 'Total Match Entries', value: `$${(stats.total_wagered || 0).toFixed(0)}`, icon: TrendingUp, color: '#CCFF00' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 + 0.15 }}
              className="glass-card rounded-xl p-5"
            >
              <div className="flex items-center gap-3 mb-2">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                </div>
              </div>
              <p className="text-white/50 text-sm">{stat.label}</p>
              <p className="font-mono text-2xl font-bold" style={{ color: stat.color }}>
                {stat.value}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Streaks & Performance */}
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card rounded-xl p-6"
          >
            <h2 className="font-heading font-semibold text-lg mb-6">Performance</h2>
            
            <div className="space-y-6">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-white/50 text-sm">Win Rate</span>
                  <span className="font-mono text-[#00FF94]">{stats.win_rate}%</span>
                </div>
                <Progress 
                  value={stats.win_rate || 0} 
                  className="h-3 bg-white/10"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-white/5">
                  <p className="text-white/50 text-xs mb-1">Current Streak</p>
                  <p className="font-mono text-xl font-bold text-[#FFB800]">
                    {stats.current_streak} {stats.current_streak === 1 ? 'Win' : 'Wins'}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <p className="text-white/50 text-xs mb-1">Best Streak</p>
                  <p className="font-mono text-xl font-bold text-[#CCFF00]">
                    {stats.best_streak} {stats.best_streak === 1 ? 'Win' : 'Wins'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="glass-card rounded-xl p-6"
          >
            <h2 className="font-heading font-semibold text-lg mb-6">Favorites</h2>
            
            <div className="space-y-4">
              {stats.favorite_game && (
                <div className="p-4 rounded-lg bg-[#7000FF]/10 border border-[#7000FF]/30">
                  <p className="text-white/50 text-xs mb-1">Most Played Game</p>
                  <p className="font-semibold text-lg text-[#7000FF]">{stats.favorite_game}</p>
                </div>
              )}
              
              <div className="p-4 rounded-lg bg-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-white/40" />
                  <p className="text-white/50 text-xs">Member Since</p>
                </div>
                <p className="font-medium">
                  {userData?.created_at 
                    ? new Date(userData.created_at).toLocaleDateString('en-US', { 
                        month: 'long', 
                        year: 'numeric' 
                      })
                    : 'N/A'
                  }
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
